import { useState } from 'react'
import zxcvbn from 'zxcvbn'
import { cx } from '@/utils/strings'
import { useTranslations } from '@/utils/locale'
import { getScoreValuesByCrackTime } from '@/components/section-password-test'

interface TableDataEntry {
  password: string
}

interface PasswordStrengthTableProps {
  readonly tableData: {
    readonly passwords: readonly TableDataEntry[]
    readonly passphrases: readonly TableDataEntry[]
    readonly heading?: string
    readonly footnote?: string
  }
}

function PasswordStrengthTable({ tableData }: PasswordStrengthTableProps) {
  const { t } = useTranslations()
  const [showPassphrases, setShowPassphrases] = useState(false)

  const { passwords: passwordsTable, passphrases: passphraseTable, heading, footnote } = tableData

  return (
    <section className="max-w-8xl px-5 lg:px-9 mx-auto">
      <div className="md:bg-white md:dark:bg-secondary-800 rounded-lg px-0 py-0 md:px-5 md:py-10 lg:px-10 lg:py-16 md:border-b-2">
        {heading && <h3>{heading}</h3>}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="[&_th]:text-left [&_th]:py-3 [&_th]:px-3 lg:[&_th]:px-8 [&_th]:first-of-type:pl-0 text-sm lg:text-base">
                <th className="w-[70%]">
                  <button onClick={() => setShowPassphrases(!showPassphrases)} className="text-base">
                    <span className="font-bwi inline-block pr-2 text-primaryBlue">&#xe95b;</span>
                    {showPassphrases ? t('Toggle to passwords') : t('Toggle to passphrases')}
                  </button>
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(showPassphrases ? passphraseTable : passwordsTable).map((example, index) => {
                const currentPassword = example.password
                const testResult = zxcvbn(currentPassword)
                const exampleCrackTime = testResult.crack_times_display.offline_slow_hashing_1e4_per_second
                const exampleScore = getScoreValuesByCrackTime(String(exampleCrackTime), currentPassword, t)

                // Only show score cell on even rows (first row of each pair)
                const showScoreCell = index % 2 === 0

                return (
                  <tr
                    key={currentPassword}
                    className="border-secondary-100 dark:border-secondary-700 last:border-b-0 [&_td]:text-xs [&_td]:lg:text-base"
                  >
                    <td
                      className={cx(
                        'py-4 px-3 font-mono bg-secondary-50 dark:bg-secondary-700 relative z-0 border-b border-white dark:border-secondary-800',
                        'after:absolute after:content-[""] after:block after:h-full after:top-0 after:bottom-0 after:left-0 z-10',
                        'after:bg-gradient-to-r after:border-r-5',
                        exampleScore.fillPercentage === 0 && 'after:w-0',
                        exampleScore.fillPercentage === 10 && 'after:w-[10%]',
                        exampleScore.fillPercentage === 23 && 'after:w-[23%]',
                        exampleScore.fillPercentage === 25 && 'after:w-[25%]',
                        exampleScore.fillPercentage === 36 && 'after:w-[36%]',
                        exampleScore.fillPercentage === 49 && 'after:w-[49%]',
                        exampleScore.fillPercentage === 62 && 'after:w-[62%]',
                        exampleScore.fillPercentage === 75 && 'after:w-[75%]',
                        exampleScore.fillPercentage === 88 && 'after:w-[88%]',
                        exampleScore.fillPercentage === 100 && 'after:w-[100%]',
                        exampleScore.score === t('strong') &&
                          'after:to-primaryBlue/30 after:from-transparent after:border-primaryBlue',
                        exampleScore.score === t('good') &&
                          'after:to-green-500/30 after:from-transparent  after:border-green-500',
                        exampleScore.score === t('weak') &&
                          'after:to-accent-gold/30 after:from-transparent after:border-accent-gold',
                        exampleScore.score === t('very weak') &&
                          'after:to-accent-melon/30 after:from-transparent after:border-accent-melon'
                      )}
                    >
                      <span className="block pb-2 last-of-type:pb-0 font-semibold relative z-10">
                        {currentPassword}
                      </span>
                    </td>
                    {showScoreCell && (
                      <td className="py-1 px-3 lg:px-8 border-b" rowSpan={2}>
                        <span
                          className={cx(
                            'inline-flex items-center px-2 py-1 rounded-full w-full justify-center  font-medium text-white capitalize',
                            exampleScore.barColor
                          )}
                        >
                          {t(`${exampleScore.score}`)}
                        </span>
                        <span className="block text-center text-xs pt-2">
                          {t('Estimated time to crack')}{' '}
                          <span className="block text-xs md:text-sm font-[500] capitalize">
                            {t(`${exampleScore.level}`)}
                          </span>
                        </span>
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        {footnote && <p className="mt-5 lg:mt-8 text-sm text-secondary-600 dark:text-secondary-300">{footnote}</p>}
      </div>
    </section>
  )
}

export default PasswordStrengthTable
